import React from "react";
import { Icon, notify, confirmDialog, EmptyState } from "../registry";
import type { SiteBundle, DomainRow, DnsRecord } from "./types";
import { recordsOf, apexOf, cleanHostname, subdomainHost, siteSlugOk, slugify, fmtDate, fmtDateTime } from "./util";
import { OFFLINE_MSG, copyText } from "./fields";
import { sampleLeads } from "./sample";
import { EntriConnect, SearchConsoleCard, selfUrl } from "./growth";
import { CallTrackingCard } from "./calls";

/* ============================================================
   Website builder — Domain + Leads tabs.
   Domain: the free subdomain (always on), "connect the domain you own" with
   the exact DNS records, a status poller (pending DNS → SSL → Live, every
   20 s while this tab is open and visible), make-primary and remove.
   We never touch email (MX/SPF/DKIM) records — the copy says so.
   "Connect automatically" (Entri, when the server has it) sits above the
   manual records, which stay as the fallback; Google Search Console is the
   last card (growth.tsx).
   ============================================================ */

const STATUS: Record<string, { label: string; cls: string; tip: string }> = {
  pending_dns: { label: "Waiting for DNS", cls: "amber", tip: "Add the records below at your registrar. It usually takes 5–30 minutes to be seen, sometimes a few hours." },
  pending_ssl: { label: "Securing (SSL)", cls: "amber", tip: "DNS is right. We're issuing the security certificate — usually a few minutes." },
  active: { label: "Live", cls: "green", tip: "" },
  error: { label: "Problem", cls: "red", tip: "" },
  removed: { label: "Removed", cls: "", tip: "" },
};
const pending = (d: DomainRow) => d.status === "pending_dns" || d.status === "pending_ssl";
function StatusChip({ s }: { s: string }) {
  const st = STATUS[s] || { label: s, cls: "" };
  return <span className={"chip " + st.cls + (st.cls === "red" ? " ws-chip-red" : "")}>{pending({ status: s } as any) && <span className="ws-pulse" />}{st.label}</span>;
}

function RecordsTable({ records }: { records: DnsRecord[] }) {
  if (!records.length) return null;
  return (
    <div className="ws-dns">
      <div className="ws-dns-row ws-dns-head"><span>Type</span><span>Name / Host</span><span>Value / Points to</span></div>
      {records.map((r, i) => (
        <div key={i} className="ws-dns-row">
          <span><b>{r.type}</b></span>
          <span className="ws-dns-cell"><code>{r.name}</code><button type="button" className="ws-ib" aria-label={"Copy name " + r.name} title="Copy" onClick={() => copyText(r.name, "Name copied")}><Icon name="clipboard" size={13} /></button></span>
          <span className="ws-dns-cell"><code>{r.value}</code><button type="button" className="ws-ib" aria-label="Copy value" title="Copy" onClick={() => copyText(r.value, "Value copied")}><Icon name="clipboard" size={13} /></button></span>
          {r.purpose && <span className="ws-dns-purpose">{r.purpose}</span>}
        </div>
      ))}
    </div>
  );
}

function RegistrarHelp({ host, target }: { host: string; target: string }) {
  const apex = apexOf(host);
  const www = host.startsWith("www.") ? host : "www." + apex;
  const cname = <><b>CNAME</b> · name <code>www</code> · value <code>{target}</code></>;
  const fwd = <>forward <code>{apex}</code> to <code>https://{www}</code> (permanent / 301)</>;
  const GUIDES: [string, React.ReactNode[]][] = [
    ["GoDaddy", [<>Sign in → <b>My Products</b> → next to the domain, <b>DNS</b>.</>, <>If a <code>www</code> CNAME already exists, edit it; otherwise <b>Add New Record</b>: {cname}. TTL 1 hour.</>, <>Scroll to <b>Forwarding</b> → <b>Add Forwarding</b>: {fwd}.</>]],
    ["Namecheap", [<><b>Domain List</b> → <b>Manage</b> → <b>Advanced DNS</b>.</>, <><b>Add New Record</b>: {cname}. Delete any old <code>www</code> record first.</>, <>Under <b>Redirect Domain</b> (or a URL Redirect Record for <code>@</code>): {fwd}.</>]],
    ["Squarespace Domains", [<><b>Domains</b> → pick the domain → <b>DNS</b> → <b>DNS Settings</b>.</>, <>Remove the Squarespace defaults for <code>www</code> only, then <b>Add record</b>: {cname}.</>, <><b>Domain forwarding</b> → add a rule: {fwd}.</>]],
    ["Cloudflare", [<>Your domain → <b>DNS</b> → <b>Records</b> → <b>Add record</b>: {cname}. Set <b>Proxy status to DNS only</b> (gray cloud).</>, <><b>Rules</b> → <b>Redirect Rules</b> → a single redirect: {fwd}.</>]],
    ["Google Workspace / other", [<>Find your registrar's <b>DNS settings</b> (sometimes “Zone editor” or “Advanced DNS”).</>, <>Add {cname}.</>, <>Find <b>Forwarding</b> or <b>Redirect</b> and {fwd}. No forwarding option? Tell us and we'll help.</>, <>Leave <b>MX</b>, <b>SPF</b>, <b>DKIM</b> and every other record alone — your email keeps working.</>]],
  ];
  return (
    <div className="ws-help">
      <div className="kicker" style={{ marginBottom: 6 }}>Step-by-step at your registrar</div>
      {GUIDES.map(([name, steps]) => (
        <details key={name} className="ws-help-item">
          <summary>{name}</summary>
          <ol>{steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
        </details>
      ))}
    </div>
  );
}

export function DomainTab({ bundle, setBundle, live, reload, active, sample = false }: { bundle: SiteBundle; setBundle: any; live: boolean; reload: () => Promise<any>; active: boolean; sample?: boolean }) {
  const DB: any = (window as any).VFP_DB;
  const site = bundle.site!;
  const root = bundle.rootDomain || "fenceworks.site";
  const target = bundle.cnameTarget || "sites." + root;
  const domains = (bundle.domains || []).filter((d) => d.status !== "removed");
  const customs = domains.filter((d) => d.kind === "custom");
  const apexes = domains.filter((d) => d.kind === "apex_redirect");
  const orphanApex = apexes.filter((a) => !customs.some((c) => apexOf(c.hostname) === a.hostname));
  const subHost = subdomainHost(bundle);
  const subPrimary = !domains.some((d) => d.is_primary && d.kind !== "subdomain" && d.status === "active");
  const [host, setHost] = React.useState("");
  const [busy, setBusy] = React.useState("");
  const [slug, setSlug] = React.useState<string | null>(null);

  const patchDomain = (d: DomainRow) => setBundle((b: SiteBundle) => ({ ...b, domains: (b.domains || []).map((x) => (x.id === d.id ? { ...x, ...d } : x)) }));
  const guard = () => { if (!live) { notify(OFFLINE_MSG); return false; } return true; };

  const add = async () => {
    if (!guard()) return;
    const h = cleanHostname(host);
    if (!/^([a-z0-9-]+\.)+[a-z]{2,}$/.test(h)) { notify("Type just the domain, like acmefence.com.", { kind: "error" }); return; }
    if (h.endsWith("." + root) || h === root) { notify(`That's already your free address. Enter a domain you own, like acmefence.com.`, { kind: "error" }); return; }
    setBusy("add");
    try { const r = await DB.siteDomainAdd(h); if (r && r.domains) setBundle((b: SiteBundle) => ({ ...b, domains: r.domains })); else await reload(); setHost(""); notify("Domain added — now add the records below at your registrar."); }
    catch (e: any) { notify(e.message || "Couldn't add that domain", { kind: "error" }); }
    setBusy("");
  };
  const check = async (d: DomainRow, quiet?: boolean) => {
    if (!live) { if (!quiet) notify(OFFLINE_MSG); return; }
    if (!quiet) setBusy("check:" + d.id);
    try {
      const r = await DB.siteDomainCheck(d.id);
      if (r && r.domain) {
        patchDomain(r.domain);
        if (!quiet) notify(r.domain.status === "active" ? `${d.hostname} is live.` : STATUS[r.domain.status] ? `${d.hostname}: ${STATUS[r.domain.status].label.toLowerCase()}.` : "Checked.");
        else if (r.domain.status === "active" && d.status !== "active") notify(`${d.hostname} is live.`, { kind: "success" });
      }
    } catch (e: any) { if (!quiet) notify(e.message || "Couldn't check right now", { kind: "error" }); }
    if (!quiet) setBusy("");
  };
  const primary = async (d: DomainRow) => {
    if (!guard()) return;
    if (!(await confirmDialog({ title: `Make ${d.hostname} your main address?`, body: `Google, your links and QR codes will use https://${d.hostname}. ${subHost} keeps working and forwards there.`, confirmLabel: "Make it main" }))) return;
    setBusy("primary:" + d.id);
    try { const r = await DB.siteDomainPrimary(d.id); if (r && r.domains) setBundle((b: SiteBundle) => ({ ...b, domains: r.domains })); else await reload(); notify(`${d.hostname} is now your main address.`); }
    catch (e: any) { notify(e.message || "Couldn't change it", { kind: "error" }); }
    setBusy("");
  };
  const remove = async (d: DomainRow) => {
    if (!guard()) return;
    const main = d.is_primary;
    if (!(await confirmDialog({ title: `Disconnect ${d.hostname}?`, body: (main ? `Your site moves back to ${subHost}. ` : "") + "The domain stays yours at your registrar — remove the CNAME there when you're ready.", confirmLabel: "Disconnect", danger: true }))) return;
    setBusy("remove:" + d.id);
    try { await DB.siteDomainRemove(d.id); await reload(); notify(`${d.hostname} disconnected.`); }
    catch (e: any) { notify(e.message || "Couldn't remove it", { kind: "error" }); }
    setBusy("");
  };
  const saveSlug = async () => {
    const s = slugify(slug || "", 40);
    if (!siteSlugOk(s)) { notify("3–40 letters, numbers or dashes.", { kind: "error" }); return; }
    if (s === site.slug) { setSlug(null); return; }
    if (!guard()) return;
    if (!(await confirmDialog({ title: `Change to ${s}.${root}?`, body: `Your old link, ${site.slug}.${root}, stops working — update anything that points at it (Google profile, social bios, printed QR codes).`, confirmLabel: "Change it", danger: true }))) return;
    setBusy("slug");
    try { await DB.siteSetSlug(s); await reload(); setSlug(null); notify(`Your free address is now ${s}.${root}.`); }
    catch (e: any) { notify(e.message || "That address isn't available", { kind: "error" }); }
    setBusy("");
  };

  /* the poller: every 20 s while this tab is open, visible, and something is pending */
  const pendingIds = domains.filter(pending).map((d) => d.id).join(",");
  const domainsRef = React.useRef(domains); domainsRef.current = domains;
  React.useEffect(() => {
    if (!active || !live || !pendingIds) return;
    const tick = async () => {
      if (document.visibilityState !== "visible") return;
      for (const d of domainsRef.current.filter(pending)) await check(d, true);
    };
    const t = setInterval(tick, 20000);
    return () => clearInterval(t);
  }, [active, live, pendingIds]);

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div className="ws-tabhead" style={{ marginBottom: 0 }}>
        <div><h2 className="display">Domain</h2><p className="muted">Put your site on the domain you already own. We host it and keep the security certificate renewed.</p></div>
      </div>

      {bundle.cloudflare === false && (
        <div className="card pad ws-amber"><b>Automatic SSL isn't configured on this server yet.</b>
          <p className="muted" style={{ fontSize: 13, margin: "4px 0 0" }}>Domains can be added but won't go live until an admin connects Cloudflare. Your free {root} address works now.</p></div>
      )}

      <div className="card pad">
        <div className="row between" style={{ gap: 10, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0 }}>
            <span className="kicker">Your free address</span>
            {slug == null ? (
              <div className="ws-host"><Icon name="building" size={15} /><code>https://{subHost}</code>{subPrimary ? <span className="chip teal">Main</span> : <span className="chip">Forwards to your domain</span>}<span className="chip green">Always on</span></div>
            ) : (
              <div className="row gap-8" style={{ marginTop: 8, flexWrap: "wrap" }}>
                <div className="ws-slug"><span>https://</span><input value={slug} autoFocus aria-label="Subdomain" onChange={(e: any) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} onKeyDown={(e: any) => e.key === "Enter" && saveSlug()} /><span>.{root}</span></div>
                <button type="button" className="btn btn-primary btn-sm" disabled={busy === "slug"} onClick={saveSlug}>{busy === "slug" ? "Saving…" : "Save"}</button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSlug(null)}>Cancel</button>
              </div>
            )}
          </div>
          {slug == null && <div className="row gap-8">
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => copyText("https://" + subHost, "Link copied")}>Copy</button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSlug(site.slug)}><Icon name="edit" size={13} />Change</button>
          </div>}
        </div>
      </div>

      <div className="card pad">
        <span className="kicker kicker-teal">Connect your domain</span>
        <h3 className="display" style={{ fontSize: 18, margin: "6px 0 6px" }}>{customs.length ? "Add another domain" : "Use the domain you already own"}</h3>
        <p className="muted" style={{ fontSize: 13.5, margin: "0 0 12px", maxWidth: 660 }}>Use the domain you already own, e.g. acmefence.com — we'll put your site on www.acmefence.com and forward acmefence.com to it. We never touch your email records.</p>
        <div className="row gap-8" style={{ flexWrap: "wrap" }}>
          <input className="input" style={{ flex: "1 1 240px", maxWidth: 360 }} value={host} placeholder="acmefence.com" autoCapitalize="none" autoCorrect="off" spellCheck={false}
            onChange={(e: any) => setHost(e.target.value)} onKeyDown={(e: any) => e.key === "Enter" && add()} />
          <button type="button" className="btn btn-primary btn-sm" disabled={!host.trim() || busy === "add"} onClick={add}><Icon name="plus" size={14} />{busy === "add" ? "Connecting…" : "Connect"}</button>
        </div>
        <p className="faint" style={{ fontSize: 12, margin: "10px 0 0" }}>Don't own one yet? Buy it at any registrar (GoDaddy, Namecheap, Squarespace, Cloudflare), in your own name — then connect it here.</p>
      </div>

      {customs.map((d) => {
        const apex = apexes.find((a) => a.hostname === apexOf(d.hostname));
        const recs = recordsOf(d);
        const shown = recs.length ? recs : [{ type: "CNAME", name: d.hostname.split(".")[0] === "www" ? "www" : d.hostname, value: target, purpose: "Points your domain at your website" }];
        const apexRecs = apex ? recordsOf(apex) : [];
        const st = STATUS[d.status];
        return (
          <div key={d.id} className="card pad ws-domain">
            <div className="row between" style={{ gap: 10, flexWrap: "wrap", alignItems: "flex-start" }}>
              <div style={{ minWidth: 0 }}>
                <div className="ws-host" style={{ marginTop: 0 }}><code style={{ fontSize: 15 }}>{d.hostname}</code><StatusChip s={d.status} />{d.is_primary && <span className="chip teal">Main</span>}</div>
                <div className="faint" style={{ fontSize: 12, marginTop: 4 }}>{d.last_checked_at ? "Checked " + fmtDateTime(d.last_checked_at) : "Added " + fmtDate(d.created_at)}{pending(d) && active ? " · checking every 20 s" : ""}</div>
              </div>
              <div className="row gap-8" style={{ flexWrap: "wrap" }}>
                {d.status !== "active" && <button type="button" className="btn btn-ghost btn-sm" disabled={busy === "check:" + d.id} onClick={() => check(d)}><Icon name="undo" size={13} />{busy === "check:" + d.id ? "Checking…" : "Check now"}</button>}
                {d.status === "active" && !d.is_primary && <button type="button" className="btn btn-outline btn-sm" disabled={busy === "primary:" + d.id} onClick={() => primary(d)}>Make main address</button>}
                {d.status === "active" && <a className="btn btn-ghost btn-sm" href={selfUrl("https://" + d.hostname + "/")} target="_blank" rel="noopener"><Icon name="arrow" size={13} />Open</a>}
                <button type="button" className="btn btn-ghost btn-sm" disabled={busy === "remove:" + d.id} onClick={() => remove(d)}>Remove</button>
              </div>
            </div>
            {d.error && <div className="ws-err">{d.error}</div>}
            {d.status !== "active" && (
              <>
                {st && st.tip && <p className="muted" style={{ fontSize: 13, margin: "12px 0 8px" }}>{st.tip}</p>}
                {(d.status === "pending_dns" || d.status === "error") && ((bundle as any).entri || sample) && (
                  <EntriConnect domain={d} live={live} sample={sample} onDone={() => { check(d, true); }} />
                )}
                {d.status === "pending_dns" && <p style={{ fontSize: 13, margin: "0 0 8px" }}><b>{((bundle as any).entri || sample) ? "Or add" : "Add"} {shown.length === 1 ? "this record" : "these records"} at your registrar{((bundle as any).entri || sample) ? " yourself" : ""}:</b></p>}
                <RecordsTable records={shown} />
              </>
            )}
            {apex && (
              <div className="ws-apex">
                <div className="row gap-8" style={{ flexWrap: "wrap" }}><code>{apex.hostname}</code><span className="faint">→ forwards to</span><code>{d.hostname}</code><StatusChip s={apex.status} /></div>
                {apex.status !== "active" && apexRecs.length > 0 && <RecordsTable records={apexRecs} />}
                {apex.status !== "active" && !apexRecs.length && <p className="faint" style={{ fontSize: 12.5, margin: "6px 0 0" }}>Set up forwarding at your registrar: <code>{apex.hostname}</code> → <code>https://{d.hostname}</code> (permanent). The steps below show where.</p>}
              </div>
            )}
            {d.status !== "active" && <RegistrarHelp host={d.hostname} target={target} />}
            <p className="faint" style={{ fontSize: 12, margin: "12px 0 0" }}><Icon name="lock" size={12} style={{ verticalAlign: -2, marginRight: 4 }} />We never touch your email (MX, SPF, DKIM) records. Only the records above.</p>
          </div>
        );
      })}
      {orphanApex.map((a) => (
        <div key={a.id} className="card pad ws-domain"><div className="ws-host" style={{ marginTop: 0 }}><code>{a.hostname}</code><span className="faint">forwarding</span><StatusChip s={a.status} /></div><RecordsTable records={recordsOf(a)} /></div>
      ))}

      <SearchConsoleCard bundle={bundle} live={live} sample={sample} />
    </div>
  );
}

/* ---------------- Leads ---------------- */
const KIND: Record<string, [string, string]> = { quote: ["Quote request", "teal"], contact: ["Contact", "violet"], refer: ["Referral", "green"], widget: ["Instant quote", "amber"], missed_call: ["Missed call", "amber"] };
export function LeadsTab({ live, go, sample, bundle }: { live: boolean; go: any; sample: boolean; bundle?: SiteBundle }) {
  const DB: any = (window as any).VFP_DB;
  const [data, setData] = React.useState<any>(sample ? sampleLeads() : null);
  const [err, setErr] = React.useState("");
  const load = React.useCallback(async () => {
    if (!live) return;
    try { setErr(""); setData(await DB.siteLeads(30)); } catch (e: any) { setErr(e.message || "Couldn't load leads"); setData({ leads: [], counts: {} }); }
  }, [live]);
  React.useEffect(() => { load(); }, [load]);
  const leads: any[] = (data && data.leads) || [];
  const c = (data && data.counts) || {};
  const kindOf = (l: any) => l.kind || (l.data && l.data.kind) || "quote";
  const count = (k: string) => (c[k] != null ? c[k] : leads.filter((l) => kindOf(l) === k).length);
  const total = c.total != null ? c.total : leads.length;
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div className="ws-tabhead" style={{ marginBottom: 0 }}>
        <div><h2 className="display">Leads from your website</h2><p className="muted">The last 30 days. Every website lead also lands in Customers and starts your speed-to-lead follow-up.</p></div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => go("ai", { tab: "automations" })}><Icon name="bolt" size={13} />Speed-to-lead settings</button>
      </div>
      <div className="rv-stats ws-lead-stats">
        <div className="card rv-stat"><span className="muted">Quote requests</span><b className="display tnum">{data ? count("quote") + (c.widget != null ? c.widget : 0) : "—"}</b><span className="faint">quote form + instant quote</span></div>
        <div className="card rv-stat"><span className="muted">Contact messages</span><b className="display tnum">{data ? count("contact") : "—"}</b><span className="faint">contact page</span></div>
        <div className="card rv-stat"><span className="muted">Total</span><b className="display tnum" style={{ color: total ? "var(--teal)" : undefined }}>{data ? total : "—"}</b><span className="faint">last 30 days</span></div>
      </div>
      {err && <div className="card pad" style={{ color: "var(--red)" }}>{err}</div>}
      {bundle && <CallTrackingCard bundle={bundle} live={live} sample={sample} />}
      <div className="card">
        {!data ? <div className="muted" style={{ padding: 18 }}>Loading leads…</div> : !leads.length ? (
          <EmptyState icon="send" title="No website leads yet" sub="Once your site is live, quote requests and messages show up here — and in Customers — the moment they come in." />
        ) : (
          <div className="ws-leads">
            {leads.map((l: any) => {
              const k = kindOf(l); const [lab, cls] = KIND[k] || [k, ""];
              const page = l.page || (l.data && l.data.page) || "";
              const cust = l.customer_id || l.customerId || (l.data && (l.data.customerId || l.data.customer_id));
              const nm = l.name || (l.data && l.data.name) || l.email || l.phone || "Website visitor";
              const when = l.created_at || l.createdAt;
              return (
                <div key={l.id || nm + when} className="ws-lead">
                  <div style={{ minWidth: 0 }}>
                    <b>{nm}</b>
                    <div className="faint" style={{ fontSize: 12 }}>{fmtDateTime(when)}{page ? <> · <code>{page}</code></> : null}{l.data && l.data.utm && l.data.utm.source ? " · from " + l.data.utm.source : ""}</div>
                  </div>
                  <span className={"chip " + cls}>{lab}</span>
                  {cust ? <button type="button" className="btn btn-outline btn-sm" onClick={() => go("customers", { focus: cust })}><Icon name="user" size={13} />Open customer</button>
                    : sample ? <button type="button" className="btn btn-ghost btn-sm" onClick={() => notify("Sample lead — real ones open the customer.")}><Icon name="user" size={13} />Open customer</button> : <span />}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
